import { getMultiTripBookingOffers } from "../../../app/api/getMultiTripBookingOffers";
import { isInfantType } from "../../../Orchestrator/Types/Enums/PassengerTypeEnum";
import { BaseAction } from "../../../core/actions/BaseAction";
import { uuid } from "../../../core/utils/Uuid";
import stopManager from "../../../Lib/Utils/StopManager";
import { determineDefaultAge, getPassengerCard } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState, updateRecoilStateWithType } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IMultiBookingOfferResponseAtom } from "../../../Orchestrator/Recoils/MultiBookingAtom";
import { PassengerPrmNeedTypeEnum, TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { AnonymousPassengerSpecification, IMultiBooking, IPassenger, IPassengerSelection, SearchStateType } from "../../../Orchestrator/Types/Index";
import { IMultiBookingOfferRequestPayload, IMultiTripBookingOfferResponse } from "../../../Orchestrator/Types/Interface/IMultiBooking";
import { preselectMultiBookingTrainsAction } from "./PrepareDefaultMultiTripTrainsAction";
import dayjs from 'dayjs';

export interface IGetMultiBookingTripOfferActionParams {
  formValues: IMultiBooking
  hasFormChanged?: boolean;
  startDate: string;
  endDate: string;
}

export type IGetMultiBookingTripOfferActionResult = IMultiBookingOfferResponseAtom | null;

export class GetMultiBookingTripOfferAction extends BaseAction<IGetMultiBookingTripOfferActionResult, IGetMultiBookingTripOfferActionParams> {
  override async run(): Promise<IGetMultiBookingTripOfferActionResult> {
    const { formValues, startDate, endDate } = this.params!;
    let result: IGetMultiBookingTripOfferActionResult = null;

    try {
      if (!formValues) {
        console.error("Form values are missing");
        return result
      }

      if (
        formValues.preferredOrigin === undefined ||
        formValues.preferredDestination === undefined ||
        formValues.passengerType === undefined ||
        formValues.outboundDepartureTime === undefined ||
        !startDate ||
        !endDate
      ) {
        console.error("Essential form values are missing");
        return result
      }

      const passengerTypes = (getRecoilStateValue("passengerTypesStateAtom") as IPassengerSelection[] || [])
        .filter((type) => type.value !== "CHILD" && !isInfantType(type.value));
      const normalizedPassengerType = formValues.passengerType?.trim().toLowerCase() || "";
      const matchedPassengerType = passengerTypes.find((type) => {
        return (
          type.id?.toLowerCase() === normalizedPassengerType ||
          type.value?.toLowerCase() === normalizedPassengerType
        );
      });

      if (!matchedPassengerType?.id) {
        console.error("Passenger type is invalid for multi booking");
        return result;
      }

      const normalizedFormValues: IMultiBooking = {
        ...formValues,
        passengerType: matchedPassengerType.id,
      };

      const isRoundTripBooking = Boolean(normalizedFormValues.inboundDepartureTime)
      const tripTypes: TrainTypeEnum[] = isRoundTripBooking ? [TrainTypeEnum.outbound, TrainTypeEnum.inbound] : [TrainTypeEnum.outbound];

      let multiBookingApiPromises = [];

      const passengers: IPassenger[] = [
        {
          externalRef: uuid(),
          age: determineDefaultAge(normalizedFormValues.passengerType!),
          type: "PERSON",
          cards: getPassengerCard(normalizedFormValues.passengerType!),
          prmNeeds: normalizedFormValues.passengerType === "wheelchair"
            ? [PassengerPrmNeedTypeEnum.Wheelchair as unknown as string]
            : [],
          id: "",
          detail: {
            firstName: "",
            lastName: "",
            email: "",
            age: 0,
            phoneNumber: "",
          }
        }
      ]

      for (const trainType of tripTypes) {
        const origin = trainType === TrainTypeEnum.outbound
          ? normalizedFormValues.preferredOrigin?.trim()
          : normalizedFormValues.preferredDestination?.trim();
        const destination = trainType === TrainTypeEnum.outbound
          ? normalizedFormValues.preferredDestination?.trim()
          : normalizedFormValues.preferredOrigin?.trim();

        const calculatedOriginFromTime = trainType === TrainTypeEnum.inbound
          ? normalizedFormValues.inboundDepartureTime?.trim()
          : normalizedFormValues.outboundDepartureTime?.trim();

        if (!origin || !destination || !calculatedOriginFromTime) {
          return result
        }

        const calculatedOriginUntilTime = this.addDepartureTime(calculatedOriginFromTime, 1)

        const payload: IMultiBookingOfferRequestPayload = {
          tripSearchCriteria: {
            origin: {
              objectType: "StopPlaceRef",
              stopPlaceRef: origin
            },
            destination: {
              objectType: "StopPlaceRef",
              stopPlaceRef: destination
            },
            travelDateRange: {
              startDate: startDate,
              endDate: endDate
            },
            direction: trainType === TrainTypeEnum.inbound ? "IN_BOUND" : "OUT_BOUND",
            originTimeRange: {
              from: calculatedOriginFromTime,
              until: calculatedOriginUntilTime
            },
            parameters: {
              numberOfResults: 2,
              numberOfResultsBefore: 0,
              numberOfResultsAfter: 2,
              ignoreRealtimeData: false
            },
          },
          offerSearchCriteria: {
            currency: "AED",
            requestedOfferParts: undefined
          },
          anonymousPassengerSpecifications: passengers.map(passenger => ({
            externalRef: passenger.externalRef,
            type: passenger.type,
            cards: passenger.cards,
            age: passenger.age,
            prmNeeds: passenger.prmNeeds as string[]
          })) || [],
        }

        multiBookingApiPromises.push(getMultiTripBookingOffers(payload));
        updateRecoilState("offersRequestAtom", payload);
      }
      updateRecoilState<IPassenger[]>("passengerStateAtom", passengers);

      const [outboundResponse, inboundResponse] = await Promise.all(multiBookingApiPromises);

      if (outboundResponse) {
        const multiBookingOfferResponse = {
          outbound: outboundResponse?.answer || null,
          inbound: inboundResponse?.answer || null
        }

        result = multiBookingOfferResponse
        updateRecoilStateWithType("multiBookingOfferResponseAtom", multiBookingOfferResponse)
        updateRecoilStateWithType("multiBookingFormValuesAtom", normalizedFormValues)
        updateRecoilStateWithType("multiBookingTravelDatesAtom", {
          start: dayjs(startDate),
          end: dayjs(endDate)
        })

        const searchState: SearchStateType = {
          from: normalizedFormValues.preferredOrigin || "",
          to: normalizedFormValues.preferredDestination || "",
          outboundDate: dayjs(startDate),
          inboundDate: isRoundTripBooking ? dayjs(endDate) : null,
          passengers: {
            [normalizedFormValues.passengerType!]: 1
          },
          calc: "",
          fromName: stopManager.getStopNameById(normalizedFormValues.preferredOrigin!) || "",
          toName: stopManager.getStopNameById(normalizedFormValues.preferredDestination!) || ""
        }
        updateRecoilState("searchStateAtom", searchState)

        setTimeout(async () => {
          preselectMultiBookingTrainsAction.setParams({
            multiBookingOfferResponse: multiBookingOfferResponse,
            formValues: normalizedFormValues
          })
          await preselectMultiBookingTrainsAction.run();
        }, 100)
      }
    } catch (error) {
      console.error("error in getting multi booking trip offers", error)
      result = null
    } finally {
      return result
    }
  }

  addDepartureTime = (time: string, hour = 1) => {
    const timeParts = time.split(':');
    let hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    hours += hour;
    if (hours >= 24) {
      hours = hours - 24;
    }

    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
  }
}

export const getMultiBookingTripOfferAction = new GetMultiBookingTripOfferAction();
